import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Collapse,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import coachService from '../services/coachService';

function CoachRow({ coach, onSearchAthlete }) {
  const [open, setOpen] = useState(false);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canExpand = coach.main_sport && coach.sport_queryable;

  const toggle = async () => {
    if (!canExpand) return;
    const next = !open;
    setOpen(next);
    if (next && !perf && !loading) {
      setLoading(true);
      setError(null);
      try {
        const res = await coachService.getPerformance(
          coach.name,
          coach.main_sport,
          coach.schools.map((s) => s.school_name).filter(Boolean)
        );
        if (res.success) {
          setPerf(res);
        } else {
          setError(res.message || '查詢失敗');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const schools = coach.schools
    .map((s) => `${s.school_name || ''}（${s.sc}）`)
    .join('、');

  return (
    <>
      <TableRow
        hover={canExpand}
        onClick={toggle}
        sx={{ cursor: canExpand ? 'pointer' : 'default' }}
      >
        <TableCell padding="checkbox">
          {canExpand && (
            <IconButton size="small">
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{coach.name}</TableCell>
        <TableCell>
          {coach.has_cert ? (
            <Chip label="有證" color="success" size="small" />
          ) : (
            <Chip label="無證" size="small" />
          )}
        </TableCell>
        <TableCell>
          {coach.main_sport || '未填'}
          {coach.main_sport && !coach.sport_queryable && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              （無成績資料）
            </Typography>
          )}
        </TableCell>
        <TableCell>{coach.second_sport || '—'}</TableCell>
        <TableCell>{schools}</TableCell>
      </TableRow>
      {canExpand && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2 }}>
                {loading && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={28} />
                  </Box>
                )}
                {error && <Alert severity="error">{error}</Alert>}
                {perf && perf.summary.length === 0 && (
                  <Alert severity="info">
                    主庫查無此教練所屬單位在「{coach.main_sport}」的成績，
                    可能是姓名比對不到或該單位無此運動成績。
                  </Alert>
                )}
                {perf && perf.summary.length > 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      帶隊成效（{coach.main_sport}，依所屬單位彙總）
                    </Typography>
                    <Table size="small" sx={{ mb: 2 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>年份</TableCell>
                          <TableCell>賽會</TableCell>
                          <TableCell>單位</TableCell>
                          <TableCell align="right">選手數</TableCell>
                          <TableCell align="right">金</TableCell>
                          <TableCell align="right">銀</TableCell>
                          <TableCell align="right">銅</TableCell>
                          <TableCell align="right">前八</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {perf.summary.map((s, i) => (
                          <TableRow key={i}>
                            <TableCell>{s.games_year}</TableCell>
                            <TableCell>{s.games_name}</TableCell>
                            <TableCell>{s.unit_name}</TableCell>
                            <TableCell align="right">{s.athlete_count}</TableCell>
                            <TableCell align="right">{s.gold}</TableCell>
                            <TableCell align="right">{s.silver}</TableCell>
                            <TableCell align="right">{s.bronze}</TableCell>
                            <TableCell align="right">{s.top8}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      成績明細（點選手姓名可查個人成績）
                    </Typography>
                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>年份</TableCell>
                            <TableCell>項目</TableCell>
                            <TableCell>選手</TableCell>
                            <TableCell>單位</TableCell>
                            <TableCell align="right">名次</TableCell>
                            <TableCell>成績</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {perf.details.map((d, i) => (
                            <TableRow key={i}>
                              <TableCell>{d.GamesYear}</TableCell>
                              <TableCell>{d.Competition_Name}</TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  sx={{ p: 0, minWidth: 0 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSearchAthlete?.(d.Athlete_Name);
                                  }}
                                >
                                  {d.Athlete_Name}
                                </Button>
                              </TableCell>
                              <TableCell>{d.Unit_Name}</TableCell>
                              <TableCell align="right">{d.Ranking || '—'}</TableCell>
                              <TableCell>{d.Score || '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CoachPanel({ onSearchAthlete }) {
  const [name, setName] = useState('');
  const [school, setSchool] = useState('');
  const [sport, setSport] = useState('');
  const [certOnly, setCertOnly] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    setPage(0);
    try {
      const res = await coachService.listCoaches(params);
      if (res.success) {
        setCoaches(res.data);
      } else {
        setError(res.message || '查詢失敗');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ has_cert: '1' });
  }, [load]);

  const handleSearch = () => {
    load({
      ...(name.trim() ? { name: name.trim() } : {}),
      ...(school.trim() ? { school: school.trim() } : {}),
      ...(sport.trim() ? { sport: sport.trim() } : {}),
      ...(certOnly ? { has_cert: '1' } : {}),
    });
  };

  const paged = coaches.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          教練成效查詢
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          名冊為實際在賽會帶隊報名、且經填報系統核對的教練，教練證與專長運動以填報資料為準。
          成效為教練所屬單位選手在其專長運動的成績；同單位若有多位教練，成績無法區分個別教練貢獻。
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="教練姓名"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="學校"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="專長運動"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={certOnly}
                  onChange={(e) => setCertOnly(e.target.checked)}
                />
              }
              label="只看有證"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              查詢
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>姓名</TableCell>
                    <TableCell>教練證</TableCell>
                    <TableCell>專長運動</TableCell>
                    <TableCell>次要運動</TableCell>
                    <TableCell>服務學校</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((c, i) => (
                    <CoachRow
                      key={`${c.name}-${c.cert_no || i}`}
                      coach={c}
                      onSearchAthlete={onSearchAthlete}
                    />
                  ))}
                  {paged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        查無資料
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={coaches.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="每頁筆數"
            />
          </>
        )}
      </Paper>
    </>
  );
}

export default CoachPanel;
